import {
  createRouter,
  createWebHashHistory,
  Router,
  RouteRecordRaw
} from "vue-router";
import Login from "../views/Login.vue";
import Home from "../views/Home.vue";
import Modules from "../views/Modules.vue";
import Exercises from "../views/Exercises.vue";
import Agenda from "../views/Agenda.vue";
import Messages from "../views/Messages.vue";
import ChangePassword from "../views/ChangePassword.vue";
import NotFound from "../views/NotFound.vue";
import ModulePage from "../views/ModulePage.vue";
import EnterOTP from "@/views/OTP/EnterOTPConfig.vue";
import LeaveOTP from "@/views/OTP/LeaveOTPConfig.vue";
import EnterInAppBrowser from "@/views/innAppBrowser/enterInAppBrowser.vue";
import CloseInAppBrowser from "@/views/innAppBrowser/closeInAppBrowser.vue";
import ApiService from "../services/ApiService";
import { wrapper } from "../utils/wrapper";
import StorageService from "@/services/StorageService";
import { handleUnauthorizedError } from "@/utils/user";
import { App } from "vue";
import { KeycloakUtil } from "@/utils/keycloak";
import { createAction, openPage, closePage } from "@/utils/patientLoggerPage";
import i18n from "../i18n";

const api = new ApiService();
const storageService = new StorageService();

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Login",
    component: Login,
    meta: {
      isAuthenticated: false
    }
  },
  {
    path: "/home",
    name: "Home",
    component: Home,
    meta: {
      isAuthenticated: true
    }
  },
  {
    path: "/modules",
    name: "Modules",
    component: Modules,
    meta: {
      isAuthenticated: true
    }
  },
  {
    path: "/exercises",
    name: "Exercises",
    component: Exercises,
    meta: {
      isAuthenticated: true
    }
  },
  {
    path: "/agenda",
    name: "Agenda",
    component: Agenda,
    meta: {
      isAuthenticated: true
    }
  },
  {
    path: "/messages",
    name: "Messages",
    component: Messages,
    meta: {
      isAuthenticated: true
    }
  },
  {
    path: "/changePassword",
    name: "ChangePassword",
    component: ChangePassword,
    meta: {
      isAuthenticated: false
    }
  },
  {
    path: "/otp",
    name: "EnterOTP",
    component: EnterOTP,
    meta: {
      isAuthenticated: false
    }
  },
  {
    path: "/leaveOTP",
    name: "LeaveOTP",
    component: LeaveOTP,
    meta: {
      isAuthenticated: false
    }
  },
  {
    path: "/enterInAppBrowser",
    name: "EnterInAppBrowser",
    component: EnterInAppBrowser,
    meta: {
      isAuthenticated: false
    }
  },
  {
    path: "/closeInAppBrowser",
    name: "CloseInAppBrowser",
    component: CloseInAppBrowser,
    meta: {
      isAuthenticated: false
    }
  },
  {
    path: "/module/:moduleId(\\d+)/page/:pageId(\\d+)/state/:pageStateId(\\d+)", // (\\d+) guarantees that both ids are numbers; the page is actually the page number
    name: "Page",
    component: ModulePage,
    meta: {
      isAuthenticated: true
    }
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFound,
    meta: {
      isAuthenticated: true
    }
  }
];

export const router = createRouter({
  history: createWebHashHistory(),
  routes
});

async function handleUnauthorizedUser(
  keycloak: KeycloakUtil,
  next: Function,
  errorTitle: string,
  errorDetail: string
) {
  handleUnauthorizedError(keycloak, errorTitle, errorDetail);

  next({ name: "Login" });
}

async function resetPassword(next: Function) {
  next({ name: "ChangePassword" });
}

async function handleAuthorizedUser(keycloak: KeycloakUtil, next: Function) {
  // save token in storage
  const token = await wrapper(keycloak.getKeycloakAccessTokenParsed());
  if (token.error) {
    throw token.error;
  }

  if (token.data) {
    storageService.set("token", token.data);
  }

  const keycloakUserProfile = await wrapper(keycloak.getUserProfile());
  let patientsId;
  let usersId;

  if (keycloakUserProfile.error) {
    console.error("Error getting user profile", keycloakUserProfile.error);
    await handleUnauthorizedUser(
      keycloak,
      next,
      "LOGIN.ERROR_RETRIEVING_USER_INFO_TITLE",
      "LOGIN.ERROR_RETRIEVING_USER_INFO_DETAIL"
    );
    return;
  }

  if (keycloakUserProfile.data && !storageService.getUser()) {
    const username = keycloakUserProfile.data.username;

    patientsId = parseInt(keycloakUserProfile.data.attributes.patientsId[0]);
    usersId = parseInt(keycloakUserProfile.data.attributes.usersId[0]);

    storageService.set("user", {
      username: username,
      patientsId: patientsId,
      usersId: usersId
    });

    const patientLoggerPageId = await wrapper(openPage("Login", patientsId));
    if (patientLoggerPageId.error) {
      console.error("Error opening logger page: ", patientLoggerPageId.error);
    }

    const patientLoggerAction = await wrapper(
      createAction(
        "login",
        patientLoggerPageId.data,
        undefined,
        undefined,
        patientsId
      )
    );

    if (patientLoggerAction.error) {
      console.error("Err creating logger action:", patientLoggerAction.error);
    }

    const patientLoggerActionClose = await wrapper(
      closePage(patientLoggerPageId.data, patientsId)
    );

    if (patientLoggerActionClose.error) {
      console.error("Err closing logger page:", patientLoggerActionClose.error);
    }
  }

  const renewToken = await wrapper(keycloak.updateToken());

  if (renewToken.error) {
    console.error(renewToken.error);
    await handleUnauthorizedUser(
      keycloak,
      next,
      "LOGIN.ERROR_RENEWING_TOKEN_TITLE",
      "LOGIN.ERROR_RENEWING_TOKEN_DETAIL"
    );
  } else {
    if (!patientsId) {
      patientsId = storageService.getUser().patientsId;
    }

    const patientInstance = await wrapper(api.getPatient(patientsId));

    if (patientInstance.error) {
      if (
        patientInstance.error.response.data.error.statusCode == 422 &&
        patientInstance.error.response.data.error.code ==
          "VALIDATION_ERROR_RESET_PASSWORD"
      ) {
        await resetPassword(next);
        return;
      } else {
        console.error("Error getting patient: ", patientInstance.error);

        await handleUnauthorizedUser(
          keycloak,
          next,
          "LOGIN.ERROR_RETRIEVING_USER_INFO_TITLE",
          "LOGIN.ERROR_RETRIEVING_USER_INFO_DETAIL"
        );
        return;
      }
    }

    if (patientInstance.data) {
      storageService.set("user", {
        username: patientInstance.data.users.username,
        patientsId: patientInstance.data.id,
        usersId: patientInstance.data.usersId,
        gender: patientInstance.data.gender,
        portalMessages: patientInstance.data.portalMessages,
        canSendMessages: patientInstance.data.canSendMessages,
        language: patientInstance.data.users.language,
        therapist: {
          id: patientInstance.data.therapistsId,
          name: patientInstance.data.therapists.name,
          usersId: patientInstance.data.therapists.usersId
        }
      });

      i18n.global.locale = patientInstance.data.users.language;

      next();
    }
  }
}

export default {
  install: (app: App): Router => {
    router.install(app);

    router.beforeResolve(async (to, from, next) => {
      const keycloak: KeycloakUtil = app.config.globalProperties.$keycloak;

      if (to.meta.isAuthenticated) {
        if (!(await keycloak.isAuthenticated())) {
          // If user not loggedIn but there is an entry in localStorage with "inAppBrowserToken" -> continue
          if (await storageService.get('inAppBrowserToken') && await storageService.get('user')) {
            next()
            return
          } 
          // else -> remove 'user' and 'token' from storage if token is expired
          const token = JSON.parse(storageService.get("token")) || undefined;
          if (token?.exp && token.exp < Date.now() / 1000) {
            storageService.delete("user");
            storageService.delete("token");
          }

          // The page is protected and the user is not authenticated. Force a login.
          // Note: isAuthenticated always returns false when the page is refreshed,
          // so technically the login redirect always happens but it's not noticeable.
          await keycloak.login(`${to.path}`);
        } else if (await keycloak.hasPermissions()) {
          // The user was authenticated, and has the app role
          await handleAuthorizedUser(keycloak, next);
        } else {
          // The user was authenticated, but did not have the correct role
          // Logouts and Redirects to an error page
          await handleUnauthorizedUser(
            keycloak,
            next,
            "LOGIN.INVALID_SESSION_TITLE",
            "LOGIN.INVALID_SESSION_DETAIL"
          );
        }
      } else {
        // This page did not require authentication
        next();
      }
    });

    return router;
  }
};
